import { Swatch } from "@aika/types";
import axios from "axios";
import Axios from "axios";
import { isEqual } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTheme } from "styled-components";
import { v4 as uuid } from "uuid";

export type Brush = {
  id: string;
  description: string | null;
  color: number;
  tags: string[];
  isSwatch: boolean;
};

type BrushesContextType = {
  brushes: Brush[];
  saveSwatch: (spec: Swatch) => void;
  removeSwatch: (id: string) => void;
  loading: boolean;
  swatches: Swatch[];
  swatchExists: (spec: Swatch) => boolean;
};

export const BrushesContext = createContext<BrushesContextType>(null!);

export const BrushesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  const [swatches, setSwatches] = useState<Swatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Axios.get("/api/swatches").then(({ data }) => {
      setSwatches(data);
      setLoading(false);
    });
  }, []);

  const colorBrushes = useMemo<Brush[]>(
    () =>
      theme.blockColors.map((_, i) => ({
        id: `color-${i}`,
        description: null,
        color: i,
        tags: [],
        isSwatch: false,
      })),
    [theme]
  );

  const swatchBrushes = useMemo<Brush[]>(
    () =>
      swatches.map((x) => ({
        id: x.id,
        description: x.description,
        color: x.color,
        tags: x.tags,
        isSwatch: true,
      })),
    [swatches]
  );

  const brushes = useMemo<Brush[]>(
    () => colorBrushes.concat(swatchBrushes),
    [colorBrushes, swatchBrushes]
  );

  const saveSwatch = useCallback((spec: Swatch) => {
    const s = { ...spec };
    if (s.id == null) {
      s.id = uuid();
    }
    axios.post("/api/swatches", s);
    setSwatches((old) => old.filter((x) => x.id !== s.id).concat([s]));
  }, []);

  const removeSwatch = useCallback((id: string) => {
    axios.delete(`/api/swatches/${id}`);
    setSwatches((old) => old.filter((x) => x.id !== id));
  }, []);

  const swatchExists = useCallback(
    (spec: Swatch) => {
      return swatches.some(
        (x) =>
          x.description === spec.description &&
          isEqual(x.tags.sort(), spec.tags.sort()) &&
          x.color === spec.color
      );
    },
    [swatches]
  );

  const value = {
    brushes,
    loading,
    saveSwatch,
    removeSwatch,
    swatches,
    swatchExists,
  };

  return (
    <BrushesContext.Provider value={value}>{children}</BrushesContext.Provider>
  );
};

export const useBrushes = () => useContext(BrushesContext);
